import { useState, useEffect } from 'react';
import { Text } from '@qwickapps/react-framework';
import { useAuth } from '@qwickapps/auth-client';
import { config } from '../../config/runtime';

interface DashboardStatsData {
  projectsCount: number;
  projectsActive: number;
  devicesCount: number;
  devicesActive: number;
  totalSessions: number;
  sessionsRunning: number;
  totalPrompts: number;
  totalExecutions: number;
  latency?: {
    median_ms: number | null;
  };
}

interface StatCardProps {
  label: string;
  value: string | number;
  subLabel?: string;
  icon: string;
  iconColor?: string;
}

function StatCard({ label, value, subLabel, icon, iconColor = 'var(--theme-primary)' }: StatCardProps) {
  return (
    <div
      data-testid="stat-card"
      style={{
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
        borderRadius: 8,
      }}
    >
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
          <Text variant="body2" content={label} customColor="var(--theme-text-secondary)" />
          <span className="material-icons" style={{ fontSize: 20, color: iconColor }}>{icon}</span>
        </div>
        <Text variant="h5" content={String(value)} />
        {subLabel && (
          <Text variant="caption" content={subLabel} customColor="var(--theme-text-secondary)" />
        )}
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div
      data-testid="skeleton"
      style={{
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div style={{ width: '60%', height: 16, borderRadius: 4, background: 'var(--theme-border-color)', animation: 'skeleton-pulse 1.5s ease-in-out infinite', marginBottom: 8 }} />
      <div style={{ width: '40%', height: 36, borderRadius: 4, background: 'var(--theme-border-color)', animation: 'skeleton-pulse 1.5s ease-in-out infinite', marginBottom: 6 }} />
      <div style={{ width: '50%', height: 12, borderRadius: 4, background: 'var(--theme-border-color)', animation: 'skeleton-pulse 1.5s ease-in-out infinite' }} />
    </div>
  );
}

export default function DashboardStats() {
  const { session } = useAuth();
  const [stats, setStats] = useState<DashboardStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.accessToken) return;

    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${config.apiUrl}/api/dashboard/stats`, {
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch stats: ${response.status}`);
        }
        const data: DashboardStatsData = await response.json();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [session?.accessToken]);

  if (error) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 8,
          background: 'var(--theme-bg-surface)',
          border: '1px solid var(--theme-border-color)',
          marginBottom: 24,
        }}
      >
        <Text variant="body2" content={`Error loading stats: ${error}`} customColor="var(--theme-error, #f44336)" />
      </div>
    );
  }

  const cards = loading || !stats
    ? Array.from({ length: 5 }, (_, i) => <StatCardSkeleton key={i} />)
    : [
        <StatCard
          key="projects"
          label="Total Projects"
          value={stats.projectsCount}
          subLabel={`${stats.projectsActive} active`}
          icon="folder"
        />,
        <StatCard
          key="devices"
          label="Active Devices"
          value={stats.devicesActive}
          subLabel={`${stats.devicesCount} total`}
          icon="devices"
          iconColor="#4caf50"
        />,
        <StatCard
          key="sessions"
          label="Running Sessions"
          value={stats.sessionsRunning}
          subLabel={`${stats.totalSessions} total`}
          icon="play_circle"
          iconColor="#2196f3"
        />,
        <StatCard
          key="prompts"
          label="Total Prompts"
          value={stats.totalPrompts}
          icon="chat"
          iconColor="#9c27b0"
        />,
        <StatCard
          key="latency"
          label="Median Latency"
          value={stats.latency?.median_ms != null ? `${Math.round(stats.latency.median_ms)}ms` : '—'}
          subLabel="execution p50"
          icon="speed"
          iconColor="#ff9800"
        />,
      ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 16,
        marginBottom: 24,
      }}
    >
      {cards}
    </div>
  );
}
